import React, { useEffect, useState, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { getEducationDetails } from "../Services/GetService";
import { deleteEducation } from "../Services/DeleteService";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import useAccessToken from "../Custom/useAccessToken";
import { formatDateUsingMoment } from "../Utils/Year";
import { saveEducation } from "../Services/EducationService";
import { Skeleton } from "primereact/skeleton";

interface EduItem {
  educationId: string;
  degree: string;
  institute: string;
  instituteWebsite: string;
  fieldOfStudy: string;
  location: string;
  startDate: string;
  endDate: string;
  programDetail: string;
  isStudying: boolean;
}

const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];
const years = Array.from({ length: 50 }, (_, i) => ({
  label: `${2025 - i}`,
  value: 2025 - i,
}));

interface ProfileCardDashboardProps {
  profileID?: string | null;
  isOwnProfile?: boolean;
}

const Education: React.FC<ProfileCardDashboardProps> = ({
  profileID,
  isOwnProfile,
}) => {
  //
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [education, setEducation] = useState<EduItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const toast = useRef<any>(null);
  const handleDeleteEducation = async (educationId?: string) => {
    const idToDelete = educationId || deleteId;
    if (!accessToken || !idToDelete) return;
    try {
      await deleteEducation(accessToken, idToDelete);
      setEducation((prev) =>
        prev.filter((edu) => edu.educationId !== idToDelete)
      );
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Education deleted successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete education",
        life: 3000,
      });
      console.error("Failed to delete education", error);
    } finally {
      setDeleteId(null);
    }
  };

  // URL validation helper function
  const isValidURL = (string: string): boolean => {
    if (!string.trim()) return true;
    try {
      const url = new URL(string);
      return (
        (url.protocol === "http:" || url.protocol === "https:") &&
        url.hostname.length > 0 &&
        url.hostname.includes(".")
      );
    } catch {
      try {
        const url = new URL(`https://${string}`);
        return url.hostname.length > 0 && url.hostname.includes(".");
      } catch {
        return false;
      }
    }
  };

  const emptyFormData = () => {
    setSidebarVisible(false);
    setEditMode(false);
    setForm({
      educationId: null,
      institute: "",
      degree: "",
      fieldOfStudy: "",
      location: "",
      instituteWebsite: "",
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      current: false,
      isStudying: false,
      description: "",
    });
    setDateErrors({
      startDate: "",
      endDate: "",
    });
  };

  // Date validation helper functions
  const validateDates = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

    const errors = {
      startDate: "",
      endDate: "",
    };

    if ((form.startMonth && !form.startYear) || (!form.startMonth && form.startYear)) {
      errors.startDate = "Please select both the start month and year, or leave both fields blank";
    }

    // Validate start date
    if (form.startMonth && form.startYear) {
      const startDate = new Date(
        form.startYear,
        parseInt(form.startMonth) - 1,
        1
      );
      const currentDateFirstDay = new Date(currentYear, currentMonth - 1, 1);

      if (startDate > currentDateFirstDay) {
        errors.startDate = "Start date cannot be in the future";
      }
    }

    if (((form.endMonth && !form.endYear) || (!form.endMonth && form.endYear))) {
      errors.endDate = "Please select both the end month and year, or leave both fields blank";
    }

    // Validate end date (only if not currently studying)
    if (!form.current && form.endMonth && form.endYear) {
      const endDate = new Date(form.endYear, parseInt(form.endMonth) - 1, 1);
      const currentDateFirstDay = new Date(currentYear, currentMonth - 1, 1);

      if (endDate > currentDateFirstDay) {
        if (!form.isStudying)
          errors.endDate = "End date cannot be in the future";
      }

      // Check if end date is before start date
      if (form.startMonth && form.startYear) {
        const startDate = new Date(
          form.startYear,
          parseInt(form.startMonth) - 1,
          1
        );
        if (endDate < startDate) {
          errors.endDate = "End date cannot be before start date";
        }
      }
    }

    if (form.current && form.endMonth && form.endYear) {
      const endDate = new Date(form.endYear, parseInt(form.endMonth) - 1, 1);
      const currentDateFirstDay = new Date(currentYear, currentMonth - 1, 1);

      if (endDate > currentDateFirstDay) {
        if (!form.isStudying) {
          errors.endDate = "End date cannot be in the future";
        }
      }

      // Check if end date is before start date
      if (form.startMonth && form.startYear) {
        const startDate = new Date(
          form.startYear,
          parseInt(form.startMonth) - 1,
          1
        );
        if (endDate < startDate) {
          if (!form.isStudying)
            errors.endDate = "End date cannot be before start date";
        }
      }
    }

    setDateErrors(errors);

    if (form.isStudying === false) return !errors.startDate && !errors.endDate;
    else return true;
  };

  const handleSubmitEducation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;

    // Validation for required fields
    if (
      !form.institute.trim() ||
      !form.degree.trim() ||
      !form.fieldOfStudy.trim() 
      // !form.startMonth || !form.startYear ||
      // (!form.isStudying && (!form.endMonth || !form.endYear))
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Missing required fields.",
        detail: "Please complete all required fields.",
        life: 4000,
      });
      return;
    }

    // URL validation
    if (form.instituteWebsite && !isValidURL(form.instituteWebsite)) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter a valid URL (e.g., https://example.com).",
        life: 4000,
      });
      return;
    }

    // Date validation
    if (!validateDates()) {
      toast.current?.show({
        severity: "error",
        summary: "Date Validation Error",
        detail: "Please check the date fields for errors",
        life: 4000,
      });
      return;
    }

    try {
      const educationData = {
        educationId: form.educationId || null,
        institute: form.institute,
        degree: form.degree,
        fieldOfStudy: form.fieldOfStudy,
        location: form.location,
        instituteWebsite: form.instituteWebsite,
        startDate:
          form.startMonth && form.startYear
            ? `${form.startYear}-${form.startMonth}-01`
            : null,
        endDate: form.isStudying
          ? null
          : form.endMonth && form.endYear
          ? `${form.endYear}-${form.endMonth}-01`
          : null,
        programDetail: form.description,
        isStudying: form.isStudying,
      };

      await saveEducation(accessToken, educationData);

      // Refresh education list
      const updatedEducation = await getEducationDetails(accessToken);
      setEducation(updatedEducation);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: editMode
          ? "Education updated successfully"
          : "Education added successfully",
        life: 3000,
      });

      // Close sidebar and reset form
      setSidebarVisible(false);
      setEditMode(false);
      setForm({
        institute: "",
        degree: "",
        fieldOfStudy: "",
        location: "",
        instituteWebsite: "",
        startMonth: null,
        startYear: null,
        endMonth: null,
        endYear: null,
        current: false,
        isStudying: false,
        description: "",
      });
      // Clear date errors when form is reset
      setDateErrors({
        startDate: "",
        endDate: "",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${editMode ? "update" : "add"} education`,
        life: 3000,
      });
      console.error(
        `Failed to ${editMode ? "update" : "add"} education`,
        error
      );
    }
  };
  const { accessToken } = useAccessToken();

  const customHeader = (
    <div className="flex align-items-center gap-2 px-2">
      <span className="font-medium text-900 text-lg">
        {editMode ? "Edit Education" : "Add Education"}
      </span>
    </div>
  );
  const [form, setForm] = useState<{
    educationId?: string | null;
    institute: string;
    degree: string;
    fieldOfStudy: string;
    location?: string;
    instituteWebsite?: string;
    startMonth: string | null;
    startYear: number | null;
    endMonth: string | null;
    endYear: number | null;
    current: boolean;
    isStudying?: boolean; // Added for checkbox binding
    description: string;
  }>({
    educationId: null,
    institute: "",
    degree: "",
    fieldOfStudy: "",
    location: "",
    instituteWebsite: "",
    startMonth: null,
    startYear: null,
    endMonth: null,
    endYear: null,
    current: false,
    isStudying: false,
    description: "",
  });
  const [dateErrors, setDateErrors] = useState({
    startDate: "",
    endDate: "",
  });
  // Helper to open sidebar in edit mode with data
  const handleEditEducation = (edu: EduItem) => {
    setEditMode(true);
    setForm({
      educationId: edu.educationId,
      institute: edu.institute || "",
      degree: edu.degree || "",
      fieldOfStudy: edu.fieldOfStudy || "",
      location: edu.location || "",
      instituteWebsite: edu.instituteWebsite || "",
      startMonth: edu.startDate ? edu.startDate.split("-")[1] : null,
      startYear: edu.startDate ? parseInt(edu.startDate.split("-")[0]) : null,
      endMonth: edu.endDate ? edu.endDate.split("-")[1] : null,
      endYear: edu.endDate ? parseInt(edu.endDate.split("-")[0]) : null,
      current: !edu.endDate,
      isStudying: edu.isStudying,
      description: edu.programDetail || "",
    });
    // Clear date errors when editing
    setDateErrors({
      startDate: "",
      endDate: "",
    });
    setSidebarVisible(true);
  };

  useEffect(() => {
    const fetchEducation = async () => {
      if (!accessToken || !profileID) return;

      try {
        setLoading(true);
        const token = accessToken;
        const education: EduItem[] = await getEducationDetails(
          token,
          profileID
        );
        setEducation(education);
      } catch (err) {
        console.error("Failed to fetch education:", err);
        setEducation([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEducation();
  }, [accessToken, profileID]);

  const handleChange = (
    field: keyof typeof form,
    value: string | number | boolean | null
  ) => {
    setForm((prev: typeof form) => ({ ...prev, [field]: value }));

    // Trigger date validation when date fields change
    if (
      [
        "startMonth",
        "startYear",
        "endMonth",
        "endYear",
        "current",
        "isStudying",
      ].includes(field as string)
    ) {
      // Use setTimeout to ensure state is updated before validation
      setTimeout(() => {
        const updatedForm = { ...form, [field]: value };
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const errors = {
          startDate: "",
          endDate: "",
        };

        if ((updatedForm.startMonth && !updatedForm.startYear) || (!updatedForm.startMonth && updatedForm.startYear)) {
          errors.startDate = "Please select both the start month and year, or leave both fields blank";
        }      

        // Validate start date
        if (updatedForm.startMonth && updatedForm.startYear) {
          const startDate = new Date(
            updatedForm.startYear,
            parseInt(updatedForm.startMonth) - 1,
            1
          );
          const currentDateFirstDay = new Date(
            currentYear,
            currentMonth - 1,
            1
          );

          if (startDate > currentDateFirstDay) {
            errors.startDate = "Start date cannot be in the future";
          }
        }

        if ( ((updatedForm.endMonth && !updatedForm.endYear) || (!updatedForm.endMonth && updatedForm.endYear))) {
          errors.endDate = "Please select both the end month and year, or leave both fields blank";
        }

        // Validate end date (only if not currently studying)
        if (
          !updatedForm.current &&
          updatedForm.endMonth &&
          updatedForm.endYear
        ) {
          const endDate = new Date(
            updatedForm.endYear,
            parseInt(updatedForm.endMonth) - 1,
            1
          );
          const currentDateFirstDay = new Date(
            currentYear,
            currentMonth - 1,
            1
          );

          if (endDate > currentDateFirstDay) {
            if (!updatedForm.isStudying)
              errors.endDate = "End date cannot be in the future";
          }

          // Check if end date is before start date
          if (updatedForm.startMonth && updatedForm.startYear) {
            const startDate = new Date(
              updatedForm.startYear,
              parseInt(updatedForm.startMonth) - 1,
              1
            );
            if (endDate < startDate) {
              if (!updatedForm.isStudying)
                errors.endDate = "End date cannot be before start date";
            }
          }
        }

        setDateErrors(errors);
      }, 0);
    }
  };

  return (
    <>
      <Toast ref={toast} baseZIndex={5000} />
      {(education?.length > 0 || isOwnProfile) && (
          <>
            <Card
              className="mb-3 shadow-1"
              title={
                <div className="flex align-items-center justify-content-between mb-4">
                  <span className="text-title font-semibold">Education</span>
                  {isOwnProfile && (
                    <Button
                      icon="pi pi-plus"
                      label="Add education"
                      size="small"
                      severity="info"
                      data-pr-tooltip="Add education"
                      data-pr-position="right"
                      aria-label="Add Education"
                      className="p-button p-component p-button-sm"
                      onClick={() => setSidebarVisible(true)}
                    />
                  )}
                </div>
              }
            >
              <div className="flex flex-column gap-4">
                {loading ? (
                  <div>
                    {["s1", "s2"].map((id) => (
                      <div
                        key={id}
                        className="p-3 flex justify-content-between gap-3 border-round surface-card mb-2"
                      >
                        <div style={{ flex: 1 }}>
                          <Skeleton width="320px" height="1.4rem" className="mb-2" />
                          <Skeleton width="180px" height="1rem" className="mb-2" />
                          <Skeleton width="220px" height="1rem" className="mb-2" />
                          <Skeleton width="420px" height="1rem" className="mb-2" />
                        </div>
                        <div>
                          <Skeleton width="120px" height="1rem" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  education.map((s: EduItem, index) => (
                    <React.Fragment key={s.educationId}>
                      <div className="flex justify-content-between gap-3">
                        <div className="flex flex-column" style={{ flex: 1 }}>
                          <span className="font-semibold text-base mb-2">
                            {s.degree}
                          </span>
                          <span className="text-sm font-medium mb-2">
                            {s.institute}
                          </span>
                          {s.instituteWebsite ? (
                            <a
                              href={
                                s.instituteWebsite.startsWith("https")
                                  ? s.instituteWebsite
                                  : `https://${s.instituteWebsite}`
                              }
                              className="text-sm mb-2 text-primary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {s.instituteWebsite}
                            </a>
                          ) : null}
                          <span className="text-sm profile-description mb-2">
                            Field of Study: {s.fieldOfStudy}
                          </span>
                          <span className="text-sm profile-description mb-2">
                            {s.startDate ? formatDateUsingMoment(s.startDate) : ""}
                            {(s.startDate && s.endDate)
                              ? ` - ${formatDateUsingMoment(s.endDate)}`
                              : ""}
                          </span>
                          <span className="text-sm line-height-2">
                            {s.programDetail}
                          </span>
                        </div>
                        <div className="flex align-items-start gap-1">
                          {isOwnProfile && (
                            <>
                              <Button
                                icon="pi pi-pencil"
                                className="p-button-text p-button shadow-none"
                                tooltip="Edit education"
                                aria-label="Edit Education"
                                onClick={() => handleEditEducation(s)}
                              />
                              <Button
                                icon="pi pi-trash"
                                className="p-button-text p-button-danger shadow-none"
                                tooltip="Delete education"
                                aria-label="Delete Education"
                                onClick={() => {
                                  confirmDialog({
                                    message:
                                      "Are you sure you want to delete this education?",
                                    header: "Confirmation",
                                    icon: "pi pi-exclamation-triangle",
                                    defaultFocus: "reject",
                                    accept: () => {
                                      void handleDeleteEducation(s.educationId);
                                    },
                                    reject: () => {
                                      toast.current?.show({
                                        severity: "warn",
                                        summary: "Cancelled",
                                        detail: "Delete cancelled",
                                        life: 3000,
                                      });
                                    },
                                  });
                                }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      {index < education.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </div>
            </Card>
            <Sidebar
              header={customHeader}
              visible={sidebarVisible}
              position="right"
              onHide={() => {
                setSidebarVisible(false);
                setEditMode(false);
                setForm({
                  educationId: null,
                  institute: "",
                  degree: "",
                  fieldOfStudy: "",
                  location: "",
                  instituteWebsite: "",
                  startMonth: null,
                  startYear: null,
                  endMonth: null,
                  endYear: null,
                  current: false,
                  isStudying: false,
                  description: "",
                });
                setDateErrors({
                  startDate: "",
                  endDate: "",
                });
              }}
              style={{ width: "55vw" }}
              showCloseIcon
              blockScroll
              className="p-0"
            >
              <Divider />
              <form>
                <div className="pt-3 px-1 flex-1 flex flex-column overflow-y-auto">
                  <div className="mb-3">
                    <label
                      htmlFor="edu-school"
                      className="block mb-2 text-900 text-base"
                    >
                      School <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      id="edu-school"
                      value={form.institute}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("institute", e.target.value)
                      }
                      placeholder="e.g., University of Tennessee"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="edu-degree"
                      className="block mb-2 text-900 text-base"
                    >
                      Degree <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      id="edu-degree"
                      value={form.degree}
                      onChange={(e) => handleChange("degree", e.target.value)}
                      placeholder="e.g., Master of Accountancy"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="edu-field"
                      className="block mb-2 text-900 text-base"
                    >
                      Field of Study <span style={{ color: "red" }}>*</span>
                    </label>
                    <InputText
                      id="edu-field"
                      value={form.fieldOfStudy}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("fieldOfStudy", e.target.value)
                      }
                      placeholder="e.g., Accounting"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="edu-location"
                      className="block mb-2 text-900 text-base"
                    >
                      Location
                    </label>
                    <InputText
                      id="edu-location"
                      value={form.location}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("location", e.target.value)
                      }
                      placeholder="e.g., Knoxville, TN"
                      className="w-full text-sm input-sm-height"
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="edu-website"
                      className="block mb-2 text-900 text-base"
                    >
                      Institute Website
                    </label>
                    <InputText
                      id="edu-website"
                      value={form.instituteWebsite}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("instituteWebsite", e.target.value)
                      }
                      placeholder="e.g., https://www.utk.edu"
                      className={`w-full text-sm input-sm-height ${form.instituteWebsite && !isValidURL(form.instituteWebsite)
                          ? "p-invalid"
                          : ""
                        }`}
                    />
                    {form.instituteWebsite && !isValidURL(form.instituteWebsite) && (
                      <small className="p-error">
                        Please enter a valid URL (e.g., https://example.com)
                      </small>
                    )}
                  </div>
                  <div className="flex gap-3 mb-0">
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="edu-startMonth"
                        className="block mb-2 text-900 text-base"
                      >
                        Start Month 
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <Dropdown
                        id="edu-startMonth"
                        value={form.startMonth}
                        options={months}
                        onChange={(e) => handleChange("startMonth", e.value)}
                        placeholder="Select month"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.startDate ? "p-invalid" : ""
                          }`}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="edu-startYear"
                        className="block mb-2 text-900 text-base"
                      >
                        Start Year 
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <Dropdown
                        id="edu-startYear"
                        value={form.startYear}
                        options={years}
                        onChange={(e) => handleChange("startYear", e.value)}
                        placeholder="Select year"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.startDate ? "p-invalid" : ""
                          }`}
                      />
                    </div>
                  </div>
                  {dateErrors.startDate && (
                    <div className="mb-0">
                      <small className="p-error">{dateErrors.startDate}</small>
                    </div>
                  )}
                  <div className="mb-4 mt-4">
                    <Checkbox
                      inputId="currentStudy"
                      checked={form.isStudying}
                      onChange={(e) => handleChange("isStudying", e.checked || false)}
                    />
                    <label
                      htmlFor="edu-currentStudy"
                      className="ml-2 text-900 text-base"
                    >
                      I am currently studying here.
                    </label>
                  </div>
                  <div className="flex gap-3 mb-0">
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="edu-endMonth"
                        className="block mb-2 text-900 text-base"
                      >
                        End Month
                        {/* {!form.isStudying && <span style={{ color: "red" }}> *</span>} */}
                      </label>
                      <Dropdown
                        id="edu-endMonth"
                        value={form.endMonth}
                        options={months}
                        onChange={(e) => handleChange("endMonth", e.value)}
                        placeholder="Select month"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.endDate ? "p-invalid" : ""
                          }`}
                        disabled={form.isStudying}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="edu-endYear"
                        className="block mb-2 text-900 text-base"
                      >
                        End Year
                        {/* {!form.isStudying && <span style={{ color: "red" }}> *</span>} */}
                      </label>
                      <Dropdown
                        id="edu-endYear"
                        value={form.endYear}
                        options={years}
                        onChange={(e) => handleChange("endYear", e.value)}
                        placeholder="Select year"
                        className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.endDate ? "p-invalid" : ""
                          }`}
                        disabled={form.isStudying}
                      />
                    </div>
                  </div>
                  {dateErrors.endDate && (
                    <div className="mb-0">
                      <small className="p-error">{dateErrors.endDate}</small>
                    </div>
                  )}
                  <div className="mb-4 mt-4">
                    <label
                      htmlFor="edu-description"
                      className="block mb-2 text-900 text-base"
                    >
                      Description
                    </label>
                    <textarea
                      id="edu-description"
                      className="p-inputtext p-component w-full text-sm"
                      style={{ minHeight: 80, resize: "vertical" }}
                      value={form.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Additional details about your education..."
                    />
                  </div>
                  <div className=" flex gap-3 mb-3 align-items-center">
                    <Button
                      label={editMode ? "Update education" : "Add education"}
                      className="p-button p-component p-button-sm"
                      icon={editMode ? "pi pi-pencil" : "pi pi-plus"}
                      type="submit"
                      onClick={handleSubmitEducation}
                    />
                    <Button
                      label="Cancel"
                      className="p-button p-component p-button-outlined p-button-sm"
                      type="button"
                      onClick={() => emptyFormData()}
                    />
                  </div>
                </div>
              </form>
            </Sidebar>
          </>
      )}
    </>
  );
};

export default Education;
